import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "./styles/motion.css";
import "./App.css";
import "./lib/i18n";

// Layout global
import Encabezado from "./components/layout/Encabezado";
import PieDePagina from "./components/layout/PieDePagina";
import WhatsAppButton from "./components/ui/WhatsAppButton";

// UI global
import BeneficioIcono from "./features/about/components/BeneficioIcono";
import CajasDeslizantes from "./components/ui/CajasDeslizantes";
import CarruselSimple from "./components/ui/CarruselSimple";
import ContadoresDestacados from "./features/about/components/ContadoresDestacados";
import Partner from "./features/about/components/Partner";
import ScrollToTop from "./components/utils/ScrollToTop";

// Features - Páginas / Vistas
import About from "./features/about/About";
import Careers from "./features/careers/Careers";
import Contact from "./features/contact/Contact";
import Services from "./features/services/Services";

// Features - Servicios de negocio
import Migracion from "./features/migracion/Migracion";
import Desarrollo from "./features/desarrollo/Desarrollo";
import Consultoria from "./features/consultoria/Consultoria";
import Data from "./features/data/Data";
import Digitalizacion from "./features/digitalizacion/Digitalizacion";
import Incident from "./features/incident/Incident";
import IA from "./features/ia/IA";
import StaffAugmentation from "./features/staff-augmentation/StaffAugmentation";
import NotFound from "./features/error/NotFound";

// Dependencias
import { motion, Variants } from "framer-motion";
import { useTranslation } from "react-i18next";
import { INTRANET_URL } from "./config/constants";

interface Beneficio {
  texto: string;
  icon: string;
  descripcion: string;
}

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.4, delayChildren: 0.2 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

// Componente que maneja la salida
const ExternalRedirect = () => {
  useEffect(() => {
    window.location.href = INTRANET_URL;
  }, []);

  return <p>Redirigiendo a la Intranet...</p>;
};

// Componente intermedio para usar useLocation
function AppContent() {
  const { t } = useTranslation();

  const location = useLocation();
  const beneficios = t("home.beneficios", {
    returnObjects: true,
  }) as Beneficio[];

  return (
    <>
      <ScrollToTop />
      <div className="container">
        <div className="background"></div>
        <Encabezado />

        <Routes>
          <Route path="/servicios" element={<Services />} />
          <Route path="/postulate" element={<Careers />} />
          <Route path="/contacto" element={<Contact />} />
          <Route path="/empresa" element={<About />} />
          <Route path="/migracion" element={<Migracion />} />
          <Route path="/desarrollo" element={<Desarrollo />} />
          <Route path="/data" element={<Data />} />
          <Route path="/digitalizacion" element={<Digitalizacion />} />
          <Route path="/incident" element={<Incident />} />
          <Route path="/ia" element={<IA />} />
          <Route path="/consultoria" element={<Consultoria />} />
          <Route path="/staffAugmentation" element={<StaffAugmentation />} />
          <Route path="/cgintranet" element={<ExternalRedirect />} />
          <Route path="*" element={<NotFound />} />

          <Route
            path="/"
            element={
              <div className="main-content">
                <CarruselSimple />

                <div
                  className="titulo-con-fondo full-width-bg"
                  style={
                    {
                      backgroundImage: "url('/102623.webp')",
                      "--bg-image": "url('/102623.webp')",
                      backgroundSize: "cover",
                      backgroundPosition: "center 60%",
                      /* Nota: Si el fondo del mundo se corta muy pronto abajo, 
                       puedes cambiar este padding a "6rem 1.5rem 10rem" */
                      padding: "6.0rem 1.5rem",
                    } as React.CSSProperties
                  }
                >
                  <div className="titulo-con-fondo-overlay"></div>

                  {/* Título principal */}
                  <h2 className="titulo-cajas">
                    {t("home.hero_linea1")}
                    <br />
                    {t("home.hero_linea2")}
                  </h2>

                  {/* Subtítulo (movido adentro) */}
                  <h3 className="titulo-cajas2">{t("home.subhero")}</h3>

                  {/* Cajas (movidas adentro para que compartan el fondo) */}
                  <CajasDeslizantes />
                </div>

                <ContadoresDestacados />

                <h5 className="empresa-subtitle3">
                  {t("home.industries_title")}
                </h5>

                <motion.section
                  className="empresa-beneficios"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: false, amount: 0.3 }}
                >
                  <motion.div
                    className="beneficios-grid"
                    variants={containerVariants}
                  >
                    {Array.isArray(beneficios) &&
                      beneficios.map((item, idx) => (
                        <motion.div key={idx} variants={itemVariants}>
                          <BeneficioIcono
                            texto={item.texto}
                            icono={item.icon}
                            descripcion={item.descripcion}
                          />
                        </motion.div>
                      ))}
                  </motion.div>
                </motion.section>

                <Partner />
              </div>
            }
          />
        </Routes>

        <PieDePagina />
        {/* Botón de WhatsApp deshabilitado temporalmente */}
        {/* <WhatsAppButton /> */}
      </div>
    </>
  );
}


export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
